import { nanoid } from 'nanoid';
import { isUnaryOperator, isListOperator } from './filterPillValidation';
import { matchesQuickFilterPattern } from './quickFilterPattern';

/**
 * Check for contradictory conditions (multiple = predicates on same field with AND)
 * Example: status = 'New' AND status = 'Active' - logically impossible
 * 
 * @param {Object} ast - AST node (must be 'and' type)
 * @returns {boolean} True if contradictory conditions detected
 */
function hasContradictoryConditions(ast) {
  if (!ast || ast.type !== 'and' || !ast.items) {
    return false;
  }
  
  // Group predicates by field and operator
  const fieldPredicates = new Map(); // field -> [{op, value}, ...]
  
  for (const item of ast.items) {
    if (item.type === 'predicate') {
      const { field, op, value } = item;
      
      // Check for contradictory equality conditions on same field
      // Multiple '=' predicates on same field with different values = contradiction
      if (op === '=') {
        if (!fieldPredicates.has(field)) {
          fieldPredicates.set(field, []);
        }
        const predicates = fieldPredicates.get(field);
        
        // Check if this value conflicts with existing '=' predicates
        for (const existing of predicates) {
          if (existing.op === '=' && existing.value !== value) {
            // Found contradictory condition: same field, same operator, different values
            return true;
          }
        }
        
        predicates.push({ op, value });
      }
    }
  }
  
  return false;
}

/**
 * Check for multiple predicates on the same date field (JIRA doesn't allow this)
 * Example: created_at >= "2025-09-29" AND created_at <= "2025-10-10" AND created_at = "2025-09-30"
 * 
 * @param {Object} ast - AST node (must be 'and' type)
 * @param {Object} schema - Query schema (required to identify date fields)
 * @returns {boolean} True if multiple date field predicates detected
 */
function hasMultipleDateFieldPredicates(ast, schema) {
  if (!ast || ast.type !== 'and' || !ast.items || !schema || !schema.fields) {
    return false;
  }
  
  // Group predicates by field
  const fieldPredicates = new Map(); // field -> count
  
  for (const item of ast.items) {
    if (item.type === 'predicate') {
      const { field } = item;
      if (!field) continue;
      
      // Check if this is a date field
      const fieldDef = schema.fields.find(f => f.name === field);
      const isDateField = fieldDef?.kind?.toLowerCase() === 'date';
      
      if (isDateField) {
        const count = fieldPredicates.get(field) || 0;
        fieldPredicates.set(field, count + 1);
      }
    }
  }
  
  // Check if any date field has multiple predicates
  // Note: A between range (>= and <=) will be converted to a single pill,
  // but if there's also another predicate (like =), that's multiple pills = complex
  for (const [field, count] of fieldPredicates.entries()) {
    if (count <= 1) continue;
    
    // Get all predicates for this specific field
    const predicates = ast.items.filter(item => 
      item.type === 'predicate' && item.field === field
    );
    
    // If we have exactly 2 predicates and they are >= and <=, that's a valid between range
    if (count === 2) {
      const ops = predicates.map(p => p.op).sort();
      if (ops[0] === '<=' && ops[1] === '>=') {
        // This is a valid between range, not multiple pills
        continue;
      }
    }
    
    // Multiple predicates on same date field (not just a between range)
    return true;
  }
  
  return false;
}

/**
 * Check if AST is complex (contains OR, NOT, nested structures, or multiple date field predicates)
 * 
 * @param {Object} ast - AST node
 * @param {Object} schema - Query schema (optional, required for date field detection)
 * @returns {boolean} True if AST is complex
 */
function isComplexAST(ast, schema = null) {
  if (!ast) return false;
  
  // Check for OR nodes (except quick filter pattern)
  if (ast.type === 'or') {
    // Quick filter pattern is allowed (it's an OR but matches our pattern)
    if (!matchesQuickFilterPattern(ast)) {
      return true;  // Other OR nodes are complex
    }
  }
  
  // Check for NOT nodes - ALL NOT nodes are complex (we don't support NOT in pills)
  if (ast.type === 'not') {
    return true;  // Any NOT node is complex, regardless of what it wraps
  }
  
  // Check nested structures
  if (ast.type === 'and' && ast.items) {
    // Check for contradictory conditions (e.g., status = 'New' AND status = 'Active')
    if (hasContradictoryConditions(ast)) {
      return true;  // Contradictory conditions are complex
    }
    
    // Check for multiple predicates on same date field (JIRA doesn't allow this)
    if (schema && hasMultipleDateFieldPredicates(ast, schema)) {
      return true;  // Multiple date field predicates are complex
    }
    
    // If any item is complex, the whole AST is complex
    for (const item of ast.items) {
      if (isComplexAST(item, schema)) {
        return true;
      }
    }
  }
  
  return false;
}

/**
 * Convert AST to filter pills and quick filter
 * Pure function - never mutates input AST
 * 
 * @param {Object} ast - Canonical AST
 * @param {Object} schema - Query schema (optional)
 * @returns {{filterPills: Array<FilterPill>, quickFilter: string, isComplex: boolean}}
 */
export function astToFilterPills(ast, schema) {
  if (!ast) {
    return { filterPills: [], quickFilter: '', isComplex: false };
  }
  
  // Check for complexity (pass schema for date field detection)
  if (isComplexAST(ast, schema)) {
    return { filterPills: [], quickFilter: '', isComplex: true };
  }
  
  const filterPills = [];
  let quickFilter = '';
  
  // AST values are already canonical (normalized in filterPillsToAST)
  // This is NOT a normalization point - we trust AST is canonical
  
  // CRITICAL: Clone AST items to avoid mutation
  // Never mutate the input AST
  let items = [];
  if (ast.type === 'and' && ast.items) {
    items = [...ast.items];  // Clone array
  } else if (ast.type === 'or' && matchesQuickFilterPattern(ast)) {
    // Entire AST is quick filter
    quickFilter = ast.items[0].value;
    return { filterPills: [], quickFilter, isComplex: false };
  } else {
    items = [ast];  // Single node as array
  }
  
  // Extract quick filter if present (set-based matching)
  const quickFilterIndex = items.findIndex(matchesQuickFilterPattern);
  if (quickFilterIndex !== -1) {
    const quickFilterNode = items[quickFilterIndex];
    quickFilter = quickFilterNode.items[0].value; // Extract value
    items.splice(quickFilterIndex, 1); // Safe: operating on cloned array
  }
  
  // User field names that need ID-to-object conversion
  const USER_FIELD_NAMES = ['generated_by_user', 'updated_by_user', 'last_executed_by'];
  const MODULE_FIELD_NAME = 'module';
  
  /**
   * Convert user ID to user object from schema
   * @param {string} userId - User ID (UUID)
   * @param {string} fieldName - Field name
   * @param {Object} schema - Query schema
   * @returns {Object|null} User object or null if not found
   */
  const convertUserIdToObject = (userId, fieldName, schema) => {
    if (!schema || !schema.fields || typeof userId !== 'string') {
      return null;
    }
    
    const fieldDef = schema.fields.find(f => f.name === fieldName);
    if (!fieldDef || !fieldDef.values || !Array.isArray(fieldDef.values)) {
      return null;
    }
    
    // Find user object by ID
    const userObj = fieldDef.values.find(u => {
      if (typeof u === 'object' && u !== null && u.id) {
        return u.id === userId;
      }
      return false;
    });
    
    return userObj || null;
  };

  /**
   * Convert module ID to module object from schema
   * @param {string} moduleId - Module ID (UUID)
   * @param {Object} schema - Query schema
   * @returns {Object|null} Module object or null if not found
   */
  const convertModuleIdToObject = (moduleId, schema) => {
    if (!schema || !schema.fields || typeof moduleId !== 'string') {
      return null;
    }
    
    const fieldDef = schema.fields.find(f => f.name === MODULE_FIELD_NAME);
    if (!fieldDef || !fieldDef.values || !Array.isArray(fieldDef.values)) {
      return null;
    }
    
    // Find module object by ID
    const moduleObj = fieldDef.values.find(m => {
      if (typeof m === 'object' && m !== null && m.id) {
        return m.id === moduleId;
      }
      return false;
    });
    
    return moduleObj || null;
  };
  
  // First, detect simple between-style ranges in AND expressions:
  // created_at >= "2025-09-29" AND created_at <= "2025-10-10"
  // should become a single pill with operator 'between' and values [start, end]
  const usedIndices = new Set();
  if (ast.type === 'and' && Array.isArray(items) && items.length >= 2) {
    // Group predicates by field
    const rangeByField = new Map();
    items.forEach((node, index) => {
      if (!node || node.type !== 'predicate') return;
      const { field, op, value } = node;
      if (!field || (op !== '>=' && op !== '<=')) {
        return;
      }
      if (!rangeByField.has(field)) {
        rangeByField.set(field, { gte: null, lte: null });
      }
      const entry = rangeByField.get(field);
      if (op === '>=') {
        entry.gte = { value, index };
      } else if (op === '<=') {
        entry.lte = { value, index };
      }
    });

    // For each field with both >= and <=, create a between pill (for date fields only)
    for (const [field, range] of rangeByField.entries()) {
      if (!range.gte || !range.lte) continue;

      const fieldDef = schema?.fields?.find(f => f.name === field);
      const isDateField = fieldDef?.kind?.toLowerCase() === 'date';
      if (!isDateField) continue;

      const startValue = range.gte.value;
      const endValue = range.lte.value;
      if (!startValue || !endValue) continue;

      // AST values are canonical {date, timezone} - pass through
      filterPills.push({
        id: nanoid(),
        field,
        operator: 'between',
        values: [startValue, endValue]
      });

      usedIndices.add(range.gte.index);
      usedIndices.add(range.lte.index);
    }
  }

  // Convert remaining predicates to filter pills
  items.forEach((pred, index) => {
    if (usedIndices.has(index)) {
      // This predicate was merged into a between pill
      return;
    }

    if (pred.type === 'predicate') {
      const { field, op, value } = pred;
      
      // Handle unary operators
      if (op === 'isEmpty' || op === 'isNotEmpty') {
        filterPills.push({
          id: nanoid(),
          field,
          operator: op
          // No values for unary operators
        });
      }
      // Handle list operators
      else if (op === 'in' || op === 'notIn') {
        const rawValues = Array.isArray(value) ? value : [value];
        const fieldMeta = schema?.fields?.find(f => f.name === field);
        const isDateField = fieldMeta?.kind?.toLowerCase() === 'date';
        
        // Convert user IDs to user objects if this is a user field
        // Convert module IDs to module objects if this is a module field
        // For date fields, AST values are canonical - preserve them
        const values = USER_FIELD_NAMES.includes(field) && schema
          ? rawValues.map(v => {
              // If value is already an object, use it; otherwise try to convert ID to object
              if (typeof v === 'object' && v !== null && v.id) {
                return v; // Already a user object
              }
              if (typeof v === 'string') {
                const userObj = convertUserIdToObject(v, field, schema);
                return userObj || v; // Return user object if found, otherwise keep original ID
              }
              return v;
            })
          : field === MODULE_FIELD_NAME && schema
            ? rawValues.map(v => {
                // If value is already an object, use it; otherwise try to convert ID to object
                if (typeof v === 'object' && v !== null && v.id) {
                  return v; // Already a module object
                }
                if (typeof v === 'string') {
                  const moduleObj = convertModuleIdToObject(v, schema);
                  return moduleObj || v; // Return module object if found, otherwise keep original ID
              }
              return v;
            })
          : isDateField
            ? rawValues // AST date values are canonical {date, timezone} - pass through
          : rawValues;
        
        filterPills.push({
          id: nanoid(),
          field,
          operator: op,
          values
        });
      }
      // Handle binary operators
      else {
        const fieldMeta = schema?.fields?.find(f => f.name === field);
        const isDateField = fieldMeta?.kind?.toLowerCase() === 'date';
        
        let pillValue = value;
        
        // For date fields, AST values are canonical {date, timezone} - pass through
        // For user fields, convert ID to object
        // For module fields, convert ID to object
        if (!isDateField && USER_FIELD_NAMES.includes(field) && schema && typeof value === 'string') {
        // Convert user ID to user object if this is a user field
          const userObj = convertUserIdToObject(value, field, schema);
          if (userObj) {
            pillValue = userObj; // Use user object instead of ID
          }
        } else if (!isDateField && field === MODULE_FIELD_NAME && schema && typeof value === 'string') {
          // Convert module ID to module object if this is a module field
          const moduleObj = convertModuleIdToObject(value, schema);
          if (moduleObj) {
            pillValue = moduleObj; // Use module object instead of ID
          }
        }
        
        filterPills.push({
          id: nanoid(),
          field,
          operator: op,
          values: [pillValue]  // Single value as array
        });
      }
    }
  });
  
  // Date redundancy check is now done earlier in isComplexAST for better performance
  // No need to check again here since isComplexAST already caught it
  return { filterPills, quickFilter, isComplex: false };
}

